import { Injectable } from '@angular/core';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { TabService } from '../tab.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { LanguageService } from '../lang/language.service';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { AsgardTab } from 'src/app/core/asgard-tab';
import { MimeTypesEnum } from 'src/app/utils/enums';
import { AsgardDocHtml } from 'src/app/core/documents/asgard-doc-html';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';

@Injectable({
  providedIn: 'root'
})
export class DocumentManagerService {
  private readonly documentStorage: Map<string, AsgardDocBase> = new Map();
  private readonly newDocumentSubj: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly readyDocumentSubj: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly closeDocumentSubj: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly currentDocumentSubj: BehaviorSubject<string | null> = new BehaviorSubject(null);
  private readonly beforeCurrentDocChangedSubj: BehaviorSubject<string | null> = new BehaviorSubject(null);

  private focusDocument: AsgardDocBase | null= null;

  get documentCreated(): Observable<string  | null> { return this.newDocumentSubj.asObservable(); }
  get documentReady(): Observable<string | null> { return this.readyDocumentSubj.asObservable(); }
  get documentRemoved(): Observable<string | null> { return this.closeDocumentSubj.asObservable(); }
  get currentDocumentChanged(): Observable<string | null> { return this.currentDocumentSubj.asObservable(); }
  get beforeDocChanged(): Observable<string | null> { return this.beforeCurrentDocChangedSubj.asObservable(); }

  constructor(private readonly tabService: TabService,
              private readonly langService: LanguageService) {
    this.tabService.closeTabObservable().subscribe(x => {
      this.removeDocument(x);
      // console.log('DocumentManagerService', x);
    });
    this.tabService.activeTabObservable().subscribe((e: AsgardTab | null) => {
      if (!e || (e && this.documentStorage.has(e.id))) {
        this.currentDocumentSubj.next(e ? e.id : null);
      }
    });
    this.tabService.beforeTabChanged().subscribe(e => {
      if (e) {
        this.beforeCurrentDocChangedSubj.next(e);
      }
    });
  }

  get currentDoc() {
    if (this.focusDocument !== null) {
      return this.focusDocument;
    }

    return this.tabService.activeTab !== null && this.documentStorage.has(this.tabService.activeTab.id) ?
      this.documentStorage.get(this.tabService.activeTab.id) : null;
  }

  get isFocusDocument() {
    return this.focusDocument !== null;
  }

  get allDocumentsIDs(): string[]{
    let ids: string[] = [];
    for (let key of this.documentStorage.keys()) {
      ids.push(key);
    }
    return ids;
  }

  createDocument<T extends AsgardDocBase>(ctor: (new (...args) => T), args: any[]): T {
    const newDoc = new ctor(...args);
    this.documentStorage.set(newDoc.documentId, newDoc);
    this.newDocumentSubj.next(newDoc.documentId);

    // for create document in second pane, after conversion complete
    // and we have another active pane
    // and we cant use this.tabService.activeTab / this.currentDoc
    const docPaneIndex = args && args.length >= 2 ? args[1] : 0;
    const activeTabByPaneIndex = this.tabService.getActiveTabByPaneIndex(docPaneIndex);
    if(activeTabByPaneIndex && newDoc.documentId === activeTabByPaneIndex.id){
      this.currentDocumentSubj.next(newDoc.documentId);
      return newDoc;
    }

    if (this.tabService.activeTab && this.tabService.activeTab.id === newDoc.documentId && this.currentDocumentSubj.value !== newDoc.documentId) {
      this.currentDocumentSubj.next(newDoc.documentId);
    }

    return newDoc;
  }

  // document which is not related to tab (lookup output, custom input, etc...)
  createFocusDocument<T extends AsgardDocBase>(ctor: (new (...args) => T), args: any[]): T {
    if (this.focusDocument !== null) {
      this.removeDocument(this.focusDocument.documentId);
    }

    const newDoc = new ctor(...args);
    this.focusDocument = newDoc;
    this.newDocumentSubj.next(newDoc.documentId);

    if (this.tabService.activeTab !== null &&
        this.tabService.activeTab.id === newDoc.documentId &&
        this.currentDocumentSubj.value !== newDoc.documentId) {
      this.currentDocumentSubj.next(newDoc.documentId);
    }

    return newDoc;
  }

  setDocumentOptins(id: string, args: any[] = []) {
    // console.log('setDocumentOptins', id, args);
    // console.log('this.currentDoc', this.currentDoc, this.currentDoc?.documentId);
    // console.log('document storage', this.documentStorage);

    let doc = this.documentStorage.get(id);

    if (!doc) {
      doc = this.currentDoc as AsgardDocBase | undefined;
    }

    // const doc = this.currentDoc;

    // console.log('doc', doc);

    const lngMpr = doc instanceof AsgardDocPdf ? this.langService.getLangById : this.langService.getLangByCode;

    if (doc && lngMpr !== null) {
      doc.setDocumentOptins(lngMpr as any, ...args);
      this.readyDocumentSubj.next(id);
    }
  }

  getDocument(id: string | null): AsgardDocBase | null | undefined {
    return this.tabService.activeTab !== null && id !== null ? this.documentStorage.get(id) : null;
  }

  getDocumentById(id: string): AsgardDocBase | null | undefined {
    return this.documentStorage.get(id) ? this.documentStorage.get(id) : null;
  }

  removeDocument(id: string | null) {
    if (this.focusDocument !== null && this.focusDocument.documentId === id) {
      this.focusDocument = null;
    } else if(id){
      this.documentStorage.delete(id);
    }

    return this.closeDocumentSubj.next(id);
  }

  getAllDocuments(type: MimeTypesEnum): AsgardDocBase[] {
    const docs: AsgardDocBase[] = [];
    this.tabService.allTabs.forEach(tab => {
      const doc = this.documentStorage.get(tab.id);
      if (doc) {
        switch (type) {
          case MimeTypesEnum.ApplicationPdf:
            if (doc instanceof AsgardDocPdf) {
              docs.push(doc);
            }
            break;
          case MimeTypesEnum.ApplicationRtf:
            if (doc instanceof AsgardDocHtml) {
              docs.push(doc);
            }
            break;
          case MimeTypesEnum.ApplicationEpub:
            if (doc instanceof AsgardDocEpub) {
              docs.push(doc);
            }
            break;
          default: break;
        }
      }
    });
    return docs;
  }
}
